import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import api from '../services/api';

const PricingContainer = styled.div`
  padding: 80px 0 60px;
  min-height: 100vh;
  background: var(--background-dark);
`;

const PricingHero = styled.div`
  background: linear-gradient(135deg, var(--background-accent) 0%, var(--background-lighter) 100%);
  padding: 60px 0;
  text-align: center;
  margin-bottom: 60px;
  border-bottom: 5px solid var(--primary-color);
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 20px;
  color: var(--text-primary);
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  span {
    color: var(--primary-color);
  }
`;

const MembershipPlansGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 80px;
  justify-content: center;
  
  & > * {
    flex: 1 1 300px;
    max-width: 350px;
  }
`;

const MembershipCard = styled(motion.div)`
  background: white;
  border: 3px solid ${props => props.$popular ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 20px;
  padding: 40px 30px;
  position: relative;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(212, 175, 55, 0.3);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -15px;
  right: 20px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 0.85rem;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
`;

const PlanName = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 15px;
  color: var(--text-primary);
`;

const PlanPrice = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 10px;
  
  span {
    font-size: 1.2rem;
    color: var(--text-secondary);
    font-weight: 400;
  }
`;

const PlanDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 25px;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
`;

const Feature = styled.li`
  padding: 12px 0;
  color: var(--text-secondary);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.95rem;
  
  svg {
    color: var(--primary-color);
    font-size: 1.1rem;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const DisabledButton = styled.div`
  display: block;
  background: var(--background-lighter);
  color: var(--text-secondary);
  padding: 15px;
  border-radius: 12px;
  font-weight: 600;
  text-align: center;
  cursor: default;
  border: 2px solid var(--border-color);
  font-size: 14px;
  line-height: 1.5;
`;

const PricingTablesSection = styled.div`
  margin-top: 80px;
`;

const ServiceTypeSection = styled.div`
  margin-bottom: 60px;
`;

const ServiceTypeTitle = styled.h3`
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 3px solid var(--primary-color);
  
  span {
    color: var(--primary-color);
  }
`;

const CategorySection = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const CategoryTitle = styled.h4`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-transform: capitalize;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const ItemCard = styled.div`
  padding: 15px;
  background: var(--background-lighter);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.2);
    border-color: var(--primary-color);
  }
`;

const ItemName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const ItemPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const ItemUnit = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-weight: 400;
  margin-left: 5px;
`;

const InfoBox = styled.div`
  background: rgba(212, 175, 55, 0.1);
  border-left: 4px solid var(--primary-color);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 0.95rem;
    line-height: 1.6;
    
    strong {
      color: var(--text-primary);
    }
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const FilterSelect = styled.select`
  padding: 12px 20px;
  font-size: 1rem;
  border: 2px solid var(--primary-color);
  border-radius: 10px;
  background: white;
  color: var(--text-primary);
  cursor: pointer;
  min-width: 200px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.2);
  }
`;

const Pricing = () => {
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      // Fetch membership plans
      const plansResponse = await api.getMembershipPlans();
      if (plansResponse.success && plansResponse.data) {
        setMembershipPlans(plansResponse.data);
      }
      
      // Fetch items
      const itemsResponse = await api.getItems();
      if (itemsResponse.success && itemsResponse.data) {
        setItems(itemsResponse.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter items by selected service type
  const filteredItems = selectedServiceType === 'all' 
    ? items 
    : items.filter(item => item.service_type === selectedServiceType);
  
  // Group items by service type and category
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.service_type]) {
      acc[item.service_type] = {};
    }
    if (!acc[item.service_type][item.category]) {
      acc[item.service_type][item.category] = [];
    }
    acc[item.service_type][item.category].push(item);
    return acc;
  }, {});
  
  const serviceTypeLabels = {
    steam_iron: 'Steam Iron',
    dry_clean: 'Dry Cleaning',
    shoes_cleaning: 'Shoes Cleaning',
    laundry: 'Laundry Services'
  };
  
  return (
    <PricingContainer>
      <PricingHero>
        <HeroTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Transparent <span>Pricing</span> for Everyone
        </HeroTitle>
        <HeroDescription
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Simple, fixed pricing for all our services. No hidden charges, no surprises!
        </HeroDescription>
      </PricingHero>

      <ContentSection>
        {/* Membership Plans */}
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span>Membership Plans</span>
        </SectionTitle>
        
        <InfoBox>
          <p><strong>👕 Daily Wear Items Only:</strong> Membership applies exclusively to Steam Iron daily wear items. All other items are charged at regular pricing.</p>
          <p style={{ marginTop: '10px' }}><strong>🎁 Bonus Clothes:</strong> Bonus clothes are available only after using all regular clothes in your plan.</p>
        </InfoBox>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : (
          <>
            <MembershipPlansGrid>
              {membershipPlans.map((plan, index) => (
                <MembershipCard
                  key={plan.id}
                  $popular={index === 1}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  {index === 1 && <PopularBadge>MOST POPULAR</PopularBadge>}
                  <PlanName>{plan.name}</PlanName>
                  <PlanPrice>₹{parseFloat(plan.price).toFixed(0)} <span>/ month</span></PlanPrice>
                  <PlanDescription>{plan.description}</PlanDescription>
                  <FeatureList>
                    <Feature><FaCheck /> {plan.clothes_limit} daily wear clothes</Feature>
                    <Feature><FaCheck /> {plan.bonus_clothes} bonus clothes FREE</Feature>
                    <Feature><FaCheck /> Total: {plan.clothes_limit + plan.bonus_clothes} clothes/month</Feature>
                    <Feature><FaCheck /> Free pickup & delivery (5km)</Feature>
                    <Feature><FaCheck /> {plan.duration_days} days validity</Feature>
                    <Feature><FaCheck /> Priority service</Feature>
                  </FeatureList>
                  <DisabledButton>📞 Contact store for activation</DisabledButton>
                </MembershipCard>
              ))}
            </MembershipPlansGrid>

            {/* Service Pricing */}
            <PricingTablesSection>
              <SectionTitle
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Complete <span>Price List</span>
              </SectionTitle>
              
              <FilterSection>
                <FilterLabel>Filter by Service:</FilterLabel>
                <FilterSelect 
                  value={selectedServiceType} 
                  onChange={(e) => setSelectedServiceType(e.target.value)}
                >
                  <option value="all">All Services</option>
                  <option value="steam_iron">Steam Iron</option>
                  <option value="dry_clean">Dry Cleaning</option>
                  <option value="shoes_cleaning">Shoes Cleaning</option>
                  <option value="laundry">Laundry Services</option>
                </FilterSelect>
              </FilterSection>

              {Object.keys(groupedItems).sort().map(serviceType => (
                <ServiceTypeSection key={serviceType}>
                  <ServiceTypeTitle>
                    {serviceTypeLabels[serviceType] || serviceType}
                  </ServiceTypeTitle>
                  
                  {Object.keys(groupedItems[serviceType]).sort().map(category => (
                    <CategorySection key={category}>
                      <CategoryTitle>{category}</CategoryTitle>
                      <ItemsGrid>
                        {groupedItems[serviceType][category]
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map(item => (
                            <ItemCard key={item.id}>
                              <ItemName>{item.item_name}</ItemName>
                              <ItemPrice>
                                ₹{parseFloat(item.price).toFixed(2)}
                                <ItemUnit>{item.unit}</ItemUnit>
                              </ItemPrice>
                            </ItemCard>
                          ))}
                      </ItemsGrid>
                    </CategorySection>
                  ))}
                </ServiceTypeSection>
              ))}
            </PricingTablesSection>
          </>
        )}
      </ContentSection>
    </PricingContainer>
  );
};

export default Pricing;
